import React, { useState, useEffect } from 'react';
import { Pagination, Empty } from 'antd';
import ViewClassComponent from '../ViewClassComponent/ViewClassComponent';
import './ClassesList.css';
import axiosInstance from '../../Config/axiosConfig';

function ClassesList({ currentFilters, searchQuery, isMobile }) {
  const [allClasses, setAllClasses] = useState([]);
  const [filteredClasses, setFilteredClasses] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [displayClasses, setDisplayClasses] = useState([]);

  // Check if any filters are active
  const isFiltering = Boolean(
    (currentFilters && Object.keys(currentFilters).length > 0) ||
    searchQuery
  );

  useEffect(() => {
    // Reset to page 1 when filters change
    if (isFiltering && currentPage !== 1) {
      setCurrentPage(1);
    } else {
      fetchClasses();
    }
  }, [currentPage, currentFilters, searchQuery]);

  const fetchClasses = async () => {
    setLoading(true);
    try {
      const payload = {
        pageNumber: currentPage, // API expects 0-based page numbers
      };

      // Apply all filters from currentFilters object
      if (currentFilters?.subjectId) {
        payload.subjectId = currentFilters.subjectId;
      }
      
      if (currentFilters?.educationClass) {
        payload.educationClass = currentFilters.educationClass;
      }
      
      if (currentFilters?.educationLevelId) {
        payload.educationClassLevel = currentFilters.educationLevelId;
      }

      console.log('Fetching classes with payload:', payload);
      
      const response = await axiosInstance.post('/Public/get-paged-classes', payload);
      console.log('Classes response:', response);
      
      if (response.data?.data?.classes) {
        let classes = response.data.data.classes;
        
        // Client-side filtering for price if needed (as backup)
        if (currentFilters?.maxPrice) {
          classes = classes.filter(classItem => {
            const price = parseFloat(classItem.price) || 0;
            return price <= currentFilters.maxPrice;
          });
        }
        
        setAllClasses(classes);
        setTotalPages(response.data.data.pages || 1);
      } else {
        setAllClasses([]);
        setTotalPages(1);
      }
    } catch (error) {
      console.error('Error fetching classes:', error);
      setAllClasses([]);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  };

  // Handle search filtering
  useEffect(() => {
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const filtered = allClasses.filter(classItem => {
        return (
          (classItem.title && classItem.title.toLowerCase().includes(query)) ||
          (classItem.instructor && classItem.instructor.toLowerCase().includes(query)) ||
          (classItem.education_class && classItem.education_class.toLowerCase().includes(query)) ||
          (classItem.description && classItem.description.toLowerCase().includes(query))
        );
      });
      setFilteredClasses(filtered);
    } else {
      setFilteredClasses(allClasses);
    }
  }, [searchQuery, allClasses]);

  // Update display classes
  useEffect(() => {
    setDisplayClasses(searchQuery ? filteredClasses : allClasses);
  }, [searchQuery, filteredClasses, allClasses]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const WireframeLoader = () => (
    <div className="grid-container" aria-busy="true" aria-live="polite">
      {[...Array(16)].map((_, i) => (
        <div key={i} className="wireframe-card">
          <div className="wireframe-image pulse-animation" />
          <div className="wireframe-line pulse-animation" style={{ width: '60%' }} />
          <div className="wireframe-line pulse-animation" style={{ width: '80%', animationDelay: '0.2s' }} />
          <div className="wireframe-line pulse-animation" style={{ width: '70%', animationDelay: '0.4s' }} />
        </div>
      ))}
    </div>
  );

  // Get filter summary
  const getActiveFiltersSummary = () => {
    const filters = [];
    if (currentFilters?.subjectId) filters.push('Subject');
    if (currentFilters?.educationLevelId) filters.push('Level');
    if (currentFilters?.educationClass) filters.push('Class');
    if (currentFilters?.maxPrice) filters.push('Price');
    
    return filters.length > 0 ? `(${filters.join(', ')})` : '';
  };

  return (
    <div className="classes-list-container">
      {loading ? (
        <WireframeLoader />
      ) : (
        <>
          {displayClasses.length > 0 ? (
            <>
              <div className="category-hor-list-grid">
                {displayClasses.map((classItem) => (
                  <ViewClassComponent 
                    key={classItem.id} 
                    name={classItem.title}
                    classData={classItem} 
                  />
                ))}
              </div>
              
              {/* Only show pagination when not filtering and not searching */}
              {!isFiltering && (
                <div className="pagination">
                  <Pagination
                    current={currentPage}
                    total={totalPages * 10} // Assuming 10 items per page from backend
                    pageSize={10}
                    onChange={handlePageChange}
                    showSizeChanger={false}
                  />
                </div>
              )}
            </>
          ) : (
            <div className="empty-state-container">
              <div className="empty-state">
                <Empty description={
                  isFiltering || searchQuery
                    ? "No classes match your search criteria. Try adjusting your filters or search terms." 
                    : "Classes Coming soon, we will keep you updated for the upcoming classes"
                } />
                {(isFiltering || searchQuery) && (
                  <button 
                    className="clear-search-button"
                    onClick={() => window.location.reload()}
                  >
                    Clear Search & Filters
                  </button>
                )}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default ClassesList;